/**
 * EmailThreadMessageCard — one message inside the email-thread conversation
 * container.
 *
 * Each card is a self-contained bordered rounded surface (CARD_GEOMETRY).
 * The header row (avatar · sender · timestamp · chevron) is the expand/collapse
 * toggle for BOTH collapsed and expanded states.
 *
 * Collapsed: header row + a one-line snippet below it (when present).
 * Expanded: header row (with recipients sub-line) + body section containing
 * the optional subject, full recipient details, message body, and reply button.
 *
 * Purely presentational: the parent owns expand state, sender labeling, and
 * the recipient summary. The body (and its iframe) mounts only while
 * expanded, keeping long threads cheap. Reply lives at the thread level.
 */

import { ChevronDown } from '@untitledui/icons';
import { memo, useCallback, useMemo, useState } from 'react';
// Non-standard interactive shapes (invisible full-row overlay trigger, inline
// chevron) that the styled lib/base Button cannot express — react-aria's
// Button keeps the keyboard/focus/press semantics. Same precedent as
// EmailThreadVisibilityBadge and the domain selects.
import { Button as AriaButton } from 'react-aria-components';

import { useOrg } from '@/contexts/OrgContext';
import { Avatar } from '@/lib/base/avatar/avatar';
import { EmailAddressTag } from '@/lib/crm/components/EmailAddressTag';
import { EmailAttachmentList, EmailIframe } from '@/lib/email';
import { isHour12, useOrgTimeFormat } from '@/lib/time';
import { cx } from '@/utils/cx';

import { EventTimestamp } from '../timeline/components/shared';
import { EmailReplyButton } from '../timeline/events/EmailReplyButton';
import {
  attachmentsForPayload,
  cardTimestampLabel,
  htmlBodyForPayload,
  initialsForLabel,
  plainTextBodyForPayload,
  snippetForPayload,
  type ThreadMessage,
} from '../timeline/threadMessages';
import {
  CARD_GEOMETRY,
  EXPANDED_BODY_PADDING,
  MESSAGE_HEADER_GEOMETRY,
} from './email-thread-geometry';

export interface EmailThreadMessageCardProps {
  message: ThreadMessage;
  /** Resolved author label — "You" for the viewer's own messages. */
  senderLabel: string;
  /**
   * Avatar initials. Defaults to initials derived from `senderLabel`;
   * pass explicitly when the label is "You" but the avatar should carry
   * the user's own initials.
   */
  avatarInitials?: string;
  /**
   * Compact humanized recipient summary ("Jennifer Walsh · cc Sarah Chen")
   * shown as "to …" in the expanded header. Null/undefined hides the line.
   */
  recipientsLine?: string | null;
  /**
   * Rendered above the body when the thread's subject changed at this
   * message (parent detects the drift). Null/undefined hides the line.
   */
  subjectLine?: string | null;
  isExpanded: boolean;
  /**
   * Toggle handler; receives the message's eventId and its CURRENT effective
   * expanded state (the same value driving `isExpanded`), so the parent can
   * flip it without needing to know which event is "latest" — keeping the
   * handler's identity independent of thread-arrival churn.
   */
  onToggle: (eventId: string, isExpanded: boolean) => void;
  /**
   * When true, the expanded body carries its own Reply action. The parent
   * sets this for inbound messages that are NOT the thread-level Reply's
   * target (the latest inbound), so replying to an earlier point in the
   * thread doesn't require the footer button to change target. The button
   * self-gates on reply eligibility (thread id, sender, org/user context).
   */
  showReply?: boolean;
}

interface RecipientRowProps {
  label: string;
  addresses: string[];
}

function RecipientRow({ label, addresses }: RecipientRowProps) {
  return (
    <div className='text-xs text-[var(--color-text-secondary)] flex flex-wrap items-center gap-x-1 gap-y-0.5'>
      <span className='text-[var(--color-text-tertiary)]'>{label}</span>
      {addresses.map(addr => (
        <EmailAddressTag key={addr} address={addr} />
      ))}
    </div>
  );
}

interface MessageBodyProps {
  htmlBody: string | undefined;
  plainBody: string | undefined;
}

/** HTML through the sandboxed iframe, else plaintext, else an empty note. */
function MessageBody({ htmlBody, plainBody }: MessageBodyProps) {
  if (htmlBody) {
    // `expandable`: a message taller than the clamp gets a "Show full
    // message" toggle instead of trapping the reader in a fixed-height
    // inner scroll.
    return (
      <EmailIframe
        html={htmlBody}
        minHeight='40px'
        maxHeight='600px'
        expandable
      />
    );
  }
  if (plainBody) {
    return (
      <div className='text-sm leading-relaxed text-[var(--color-text-primary)] whitespace-pre-wrap break-words'>
        {plainBody}
      </div>
    );
  }
  return (
    <div className='text-xs text-[var(--color-text-tertiary)] italic'>
      No message content
    </div>
  );
}

// Memoized: every prop below is either a primitive or a stable reference
// (message comes straight from useThreadTimeline's messages array, onToggle
// is a stable parent callback), so an unaffected row's shallow-equal props
// let it skip re-rendering when a SIBLING row toggles or the thread's
// lookups update without changing this row's own displayed content.
export const EmailThreadMessageCard = memo(function EmailThreadMessageCard({
  message,
  senderLabel,
  avatarInitials,
  recipientsLine,
  subjectLine,
  isExpanded,
  onToggle,
  showReply = false,
}: EmailThreadMessageCardProps) {
  const { payload, eventId } = message;
  const { timeFormat } = useOrgTimeFormat();
  const { currentOrg } = useOrg();
  const [showDetails, setShowDetails] = useState(false);

  const handleToggle = useCallback(
    () => onToggle(eventId, isExpanded),
    [onToggle, eventId, isExpanded]
  );
  const handleToggleDetails = useCallback(
    () => setShowDetails(prev => !prev),
    []
  );

  const htmlBody = htmlBodyForPayload(payload);
  const plainBody = plainTextBodyForPayload(payload);
  const snippet = snippetForPayload(payload);
  const attachments = useMemo(() => attachmentsForPayload(payload), [payload]);
  const initials = useMemo(
    () => avatarInitials ?? initialsForLabel(senderLabel),
    [avatarInitials, senderLabel]
  );

  const hour12 = isHour12(timeFormat);
  const fullDateLabel = useMemo(() => {
    const date = new Date(message.occurredAt);
    if (Number.isNaN(date.getTime())) {
      return undefined;
    }
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
      hour12,
    }).format(date);
  }, [message.occurredAt, hour12]);

  const bodyId = `email-message-body-${eventId}`;
  const recipientsId = `email-message-recipients-${eventId}`;
  const hasDetails =
    (payload.to?.length ?? 0) > 0 || (payload.cc?.length ?? 0) > 0;

  return (
    <article
      aria-label={`Message from ${senderLabel}`}
      className={CARD_GEOMETRY}
    >
      {/* Overlay-trigger pattern (see CollapsibleSection): the whole header
          toggles via an invisible full-row button, while the timestamp and
          the recipient-details disclosure sit in a sibling layer with
          pointer-events re-enabled — nesting interactive elements inside a
          <button> would be invalid HTML. */}
      <div className='relative transition-colors hover:bg-[var(--color-bg-secondary)]'>
        <AriaButton
          onPress={handleToggle}
          aria-expanded={isExpanded}
          aria-controls={isExpanded ? bodyId : undefined}
          aria-label={`${isExpanded ? 'Collapse' : 'Expand'} message from ${senderLabel}`}
          className='absolute inset-0 w-full focus-visible:ring-inset'
        />
        <div
          className={cx(
            'pointer-events-none relative',
            MESSAGE_HEADER_GEOMETRY
          )}
        >
          <Avatar size='xs' initials={initials} alt={senderLabel} />
          <span className='flex-1 min-w-0'>
            <span className='block text-sm font-medium text-[var(--color-text-primary)] truncate'>
              {senderLabel}
            </span>
            {/* Collapsed preview: the snippet sits directly under the name in
                the SAME column (not a separate block), so the collapsed card is
                a tight two-line row that lines up with the expanded header. */}
            {!isExpanded && snippet && (
              <span className='block truncate text-xs text-[var(--color-text-tertiary)]'>
                {snippet}
              </span>
            )}
            {isExpanded &&
              recipientsLine &&
              (hasDetails ? (
                // The WHOLE recipients line is the details toggle — the
                // names/emails themselves, not only the tiny chevron. A
                // 12px chevron alone is a sniper target; the text is what
                // the reader's eye (and cursor) is already on.
                <AriaButton
                  onPress={handleToggleDetails}
                  aria-expanded={showDetails}
                  aria-controls={showDetails ? recipientsId : undefined}
                  aria-label={
                    showDetails
                      ? 'Hide recipient details'
                      : 'Show recipient details'
                  }
                  className='pointer-events-auto flex max-w-full items-center gap-1 min-w-0 rounded text-[var(--color-text-tertiary)] hover:text-[var(--color-text-secondary)]'
                >
                  <span className='min-w-0 truncate text-xs'>
                    to {recipientsLine}
                  </span>
                  <ChevronDown
                    aria-hidden='true'
                    className={cx(
                      'size-3 shrink-0 transition-transform',
                      showDetails && 'rotate-180'
                    )}
                  />
                </AriaButton>
              ) : (
                <span className='flex items-center gap-1 min-w-0'>
                  <span className='text-xs text-[var(--color-text-tertiary)] truncate'>
                    to {recipientsLine}
                  </span>
                </span>
              ))}
          </span>
          <span
            className={cx(
              'pointer-events-auto shrink-0',
              // Expanded: top-align the timestamp with the sender name (the
              // sub-line below is the recipients). Collapsed: center it against
              // the two-line name+snippet stack.
              isExpanded && 'self-start pt-0.5'
            )}
            title={fullDateLabel}
          >
            <EventTimestamp
              timestamp={message.occurredAt}
              override={cardTimestampLabel(message.occurredAt, hour12)}
            />
          </span>
          <ChevronDown
            aria-hidden='true'
            className={cx(
              'size-4 shrink-0 text-[var(--color-text-tertiary)] transition-transform',
              isExpanded && 'rotate-180'
            )}
          />
        </div>
      </div>

      {isExpanded && (
        <div
          id={bodyId}
          className={cx(
            EXPANDED_BODY_PADDING,
            'flex flex-col gap-2.5 border-t border-[var(--color-border-secondary)]',
            // Ease the body in on expand instead of hard-popping it open; the
            // stable header above stays put while this reveals. motion-safe so
            // reduced-motion users get an instant swap.
            'motion-safe:animate-in motion-safe:fade-in-0 motion-safe:slide-in-from-top-1 motion-safe:duration-200 motion-safe:ease-out'
          )}
        >
          {subjectLine && (
            <p className='text-sm font-medium text-[var(--color-text-primary)]'>
              {subjectLine}
            </p>
          )}
          {showDetails && hasDetails && (
            <div
              id={recipientsId}
              className='flex flex-col gap-1.5 rounded-md bg-[var(--color-bg-secondary)] px-2.5 py-2'
            >
              {payload.to && payload.to.length > 0 && (
                <RecipientRow label='To:' addresses={payload.to} />
              )}
              {payload.cc && payload.cc.length > 0 && (
                <RecipientRow label='Cc:' addresses={payload.cc} />
              )}
            </div>
          )}
          <MessageBody htmlBody={htmlBody} plainBody={plainBody} />
          {attachments.length > 0 && currentOrg && (
            <EmailAttachmentList
              attachments={attachments}
              orgId={currentOrg.id}
            />
          )}
          {showReply && (
            <EmailReplyButton
              entityId={message.entityId}
              replyToEventId={eventId}
              payload={payload}
              className='self-start'
            />
          )}
        </div>
      )}
    </article>
  );
});
